/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ActivityExpanded
 * ----------------
 * The activity drawer grown to full screen, for exploring the route: a large
 * map (km markers, own zoom + fit buttons), an elevation
 * strip under it whose hover — or the route replay — moves a cursor along the
 * map, and a right rail with the numbers. In the rail only the splits list
 * scrolls; hovering a split highlights that kilometre on the map and clicking
 * it zooms there.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FeedIcon from './FeedIcon';
import RouteMap, { MAP_CREDIT } from './RouteMap';
import ElevationChart from './ElevationChart';
import ActivityExtras from './ActivityExtras';
import { useSplitRows, splitBarWidth, SplitsHeader, AVG_PACE_NOTE } from './ActivitySplits';
import { Skeleton } from './FeedParts';
import {
  typeMeta, displayTitle, formatDateTime, formatPace, detailStats, NO_ELEVATION_TYPES,
  pointAtDistance, routeSlice,
} from '../../pages/feed/feedModel';

const REPLAY_MS = 12000;
const FIT_PADDING = 28;

export default function ActivityExpanded({
  activity, best, route, routeLoading, elevRoute, elevLoading, maxElev, onCollapse, onClose, collapseRef,
}) {
  const meta = typeMeta(activity.type);
  const [showKm, setShowKm] = useState(true);
  const [hoverKm, setHoverKm] = useState(null);
  const [playKm, setPlayKm] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [split, setSplit] = useState(null);
  const mapRef = useRef(null);
  const raf = useRef(0);
  const onMap = useCallback((m) => { mapRef.current = m; }, []);

  const hasMap = route.length >= 2;
  const totalKm = hasMap ? route[route.length - 1].distM / 1000 : 0;
  const positions = useMemo(() => route.map((p) => [p.lat, p.lng]), [route]);
  const showElev = hasMap && !elevLoading && !NO_ELEVATION_TYPES.includes(activity.type);
  // Same rule as the drawer and the app: shown whenever the activity has splits (see ActivitySplits).
  const { rows, best: bestPace, worst, avg, isLoading: splitsLoading } = useSplitRows(activity.id, true);

  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  const togglePlay = () => {
    if (playing) { cancelAnimationFrame(raf.current); setPlaying(false); return; }
    const from = playKm !== null && playKm < totalKm ? playKm / totalKm : 0;
    const t0 = performance.now() - from * REPLAY_MS;
    setPlaying(true);
    const step = (now) => {
      const f = Math.min(1, (now - t0) / REPLAY_MS);
      setPlayKm(f * totalKm);
      if (f < 1) raf.current = requestAnimationFrame(step); else setPlaying(false);
    };
    raf.current = requestAnimationFrame(step);
  };

  const cursorKm = hoverKm !== null ? hoverKm : playKm;
  const cursorPt = hasMap && cursorKm !== null ? pointAtDistance(route, cursorKm * 1000) : null;
  const cursor = cursorPt ? [cursorPt.lat, cursorPt.lng] : null;
  const highlight = useMemo(() => (split ? routeSlice(route, split.startM, split.startM + split.distM) : null), [route, split]);

  let readout = 'Hover the profile, or press play to replay the route';
  if (cursorPt) {
    const m = cursorKm * 1000;
    const alt = pointAtDistance(elevRoute, m)?.alt;
    const row = rows.find((r) => m >= r.startM && m < r.startM + r.distM);
    readout = [`${cursorKm.toFixed(2)} km`, Number.isFinite(alt) && `${Math.round(alt)} m`, row && row.pace !== null && `${formatPace(row.pace)} /km`].filter(Boolean).join(' · ');
  }

  const zoomBy = (d) => { const m = mapRef.current; if (m) m.setZoom(m.getZoom() + d); };
  const fit = () => { const m = mapRef.current; if (m && positions.length >= 2) m.fitBounds(positions, { padding: [FIT_PADDING, FIT_PADDING] }); };
  const zoomToSplit = (s) => {
    const seg = routeSlice(route, s.startM, s.startM + s.distM);
    if (mapRef.current && seg.length >= 2) mapRef.current.fitBounds(seg, { padding: [60, 60], maxZoom: 17 });
  };

  return (
    <>
      <header className="fd-x-top">
        <button type="button" className="fd-pillbtn" onClick={onCollapse} ref={collapseRef} aria-label="Collapse to side panel">
          <FeedIcon name="close_fullscreen" /><span className="l">Collapse</span>
        </button>
        <span className="fd-x-sep" aria-hidden="true" />
        <span className="fd-tico"><FeedIcon name={meta.icon} /></span>
        <div className="fd-x-title">
          <h2 id="fd-drawer-title">{displayTitle(activity)}{best && <> <span className="fd-badge" style={{ verticalAlign: 'middle' }}><FeedIcon name="emoji_events" fill />{best}</span></>}</h2>
          <p>{meta.label} · {formatDateTime(activity.startedAt)}</p>
        </div>
        <span className="fd-x-grow" />
        {hasMap && (
          <button type="button" className={`fd-pillbtn fd-toggle${showKm ? ' on' : ''}`} aria-pressed={showKm} onClick={() => setShowKm((v) => !v)} aria-label="Km markers">
            <FeedIcon name="location_on" /><span className="l">Km markers</span>
          </button>
        )}
        <button type="button" className="fd-iconbtn" onClick={onClose} aria-label="Close"><FeedIcon name="close" /></button>
      </header>

      <div className="fd-x-body">
        <div className="fd-x-main">
          <div className="fd-x-map">
            {routeLoading && <div className="fd-skel" style={{ position: 'absolute', inset: 0, borderRadius: 0 }} />}
            {!routeLoading && hasMap && (
              <RouteMap
                route={route} interactive showKm={showKm} cursor={cursor} highlight={highlight}
                zoomControl={false} onMap={onMap} label={`Route of ${displayTitle(activity)}`}
              />
            )}
            {!routeLoading && !hasMap && (
              <div className="fd-media nomap" style={{ height: '100%', margin: 0, border: 0, borderRadius: 0 }}><span><FeedIcon name="location_off" />No GPS route recorded</span></div>
            )}
            {hasMap && (
              <>
                <div className="fd-x-ctl">
                  <button type="button" onClick={() => zoomBy(1)} aria-label="Zoom in"><FeedIcon name="add" /></button>
                  <button type="button" onClick={() => zoomBy(-1)} aria-label="Zoom out"><FeedIcon name="remove" /></button>
                  <button type="button" onClick={fit} aria-label="Fit whole route"><FeedIcon name="fit_screen" /></button>
                </div>
                <div className="fd-x-legend">
                  <span><i className="st" />Start</span>
                  <span><i className="en" />Finish</span>
                  <span><i className="rt" />Route</span>
                </div>
                <span className="fd-x-credit">{MAP_CREDIT}</span>
              </>
            )}
          </div>
          {showElev && (
            <div className="fd-x-elev">
              <div className="fd-x-elev-h">
                <button type="button" className="fd-x-play" onClick={togglePlay} aria-label={playing ? 'Pause route replay' : 'Replay route'}>
                  <FeedIcon name={playing ? 'pause' : 'play_arrow'} fill />
                </button>
                <span className="fd-eyebrow">Elevation · replay route</span>
                {maxElev !== null && <span className="fd-eyebrow fd-x-max">max {Math.round(maxElev)} m</span>}
                <span className="fd-x-grow" />
                <span className="fd-x-readout" aria-live="polite">{readout}</span>
              </div>
              <ElevationChart route={elevRoute} maxElev={maxElev} header={false} onHover={setHoverKm} cursorKm={playKm} className="fd-x-chart" />
            </div>
          )}
        </div>

        <aside className="fd-x-rail" aria-label="Activity numbers">
          <div className="fd-d-grid">
            {detailStats(activity, maxElev).map(([k, v, u]) => (
              <div key={k}><span className="fd-eyebrow">{k}</span><b>{v}{u && <small>{u}</small>}</b></div>
            ))}
          </div>
          {activity.notes && <p className="fd-d-notes">{activity.notes}</p>}
          {splitsLoading && <div className="fd-d-sec"><Skeleton height={120} /></div>}
          {!splitsLoading && rows.length > 0 && (
            <section className="fd-x-splits" aria-label="Splits per km">
              <SplitsHeader avg={avg} />
              {hasMap && <p className="fd-x-hint">Hover a split to highlight it on the map · click to zoom</p>}
              <div className="fd-x-splits-list" tabIndex={0} aria-label="Splits list, scrollable">
                {rows.slice(0, 50).map((s) => (
                  <button
                    type="button"
                    key={s.n}
                    className={`fd-split${s.pace !== null && s.pace === bestPace ? ' best' : ''}${split && split.n === s.n ? ' on' : ''}`}
                    onMouseEnter={() => setSplit(s)} onMouseLeave={() => setSplit(null)}
                    onFocus={() => setSplit(s)} onBlur={() => setSplit(null)}
                    onClick={() => zoomToSplit(s)}
                    aria-label={`Kilometre ${s.n}, pace ${formatPace(s.pace)} per km`}
                  >
                    <span>{s.n}</span>
                    <span className="tr"><i style={{ width: splitBarWidth(s.pace, bestPace, worst) }} /></span>
                    <span style={{ textAlign: 'right' }}>{formatPace(s.pace)}</span>
                  </button>
                ))}
              </div>
              <p className="fd-split-note">{AVG_PACE_NOTE}</p>
            </section>
          )}
          <ActivityExtras activity={activity} />
        </aside>
      </div>
    </>
  );
}
